import React, { useEffect, useState } from "react";
import axios from "axios";
import classnames from "classnames";
import { useRouter } from "next/router";
import { stringify } from "qs";
import { useSelector } from "react-redux";

import consumerApi from "api/services/consumer-api";
import { Category } from "constants/events";
import { MenuItem, MenuItemFilter, MenuItemSort } from "custom-types/MenuItem";
import { MenuResponse } from "custom-types/Search";
import { getCoordinates } from "redux/selectors/location";
import logError from "utils/logError";

import Carousel from "components/botanic/Carousel";
import MenuItemCard from "components/botanic/MenuItemCard";
import { MenuCardSkeleton } from "components/Shared/Skeletons";
import TrackImpression from "components/TrackImpression";

import { MenuItemCardActions } from "./MenuItemCardActions";

type Props = {
  actionHref?: string;
  actionOnClick?: (e: React.KeyboardEvent | React.MouseEvent) => void;
  actionText?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  cardOnClick?: (menuItem: any) => void;
  cardsPerSlide?: number;
  className?: string;
  filter?: MenuItemFilter;
  header: string;
  headerLink?: string;
  headerOnClick?: () => void;
  query?: string | string[] | undefined;
  showOnlineOrdering?: boolean;
  slug?: string;
  sort?: MenuItemSort;
  subheader?: string;
  viewDetailsOnClick?: (menuItem: MenuItem) => void;
};

const MenuItemCarousel: React.FC<Props> = ({
  actionHref,
  actionOnClick = () => {},
  actionText,
  cardOnClick = () => {},
  cardsPerSlide = 4,
  className,
  filter = {},
  header,
  headerLink,
  headerOnClick = () => {},
  query,
  showOnlineOrdering,
  slug,
  sort,
  subheader,
  viewDetailsOnClick = () => {},
}) => {
  const [loading, setLoading] = useState(false);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  const coordinates = useSelector(getCoordinates);

  const {
    query: { shopCategory, showSimilar, shopName },
  } = useRouter();

  const showSimilarStrains =
    shopCategory === "strain" &&
    (showSimilar === "on" || showSimilar === "menu_items_only");

  useEffect(() => {
    const source = axios.CancelToken.source();

    const fetchMenuItems = async () => {
      try {
        setLoading(true);

        const boostQuery = showSimilarStrains
          ? { boost: "similar_menu_items" }
          : {};
        const queryString = stringify(
          {
            filter,
            q: query,
            sort: sort,
            take: 8,
            ...boostQuery,
            ...coordinates,
          },
          { arrayFormat: "brackets", encodeValuesOnly: true }
        );

        const response = await consumerApi.get<MenuResponse>(
          `/api/dispensaries/v1/${slug}/menu_items?${queryString}`,
          {
            cancelToken: source.token,
          }
        );

        const {
          data,
          metadata: { totalCount },
        } = response.data;

        setLoading(false);
        setMenuItems(data);
        setTotalCount(totalCount);
      } catch (e) {
        if (e.statusCode !== "ERR_CANCELED") {
          logError(e.message, {
            functionName: "MenuItemCarousel",
            service: "consumer",
            statusCode: e.statusCode,
          });
        }

        setLoading(false);
        setMenuItems([]);
        setTotalCount(null);
      }
    };

    fetchMenuItems();

    return () => {
      // Cancel the axios request
      source.cancel();
    };
  }, [
    JSON.stringify(coordinates),
    JSON.stringify(filter),
    query,
    showSimilarStrains,
    slug,
    sort,
  ]);

  if (!menuItems.length && !loading) {
    return null;
  }

  const updatedActionText = actionText?.replace(
    "%tc",
    totalCount?.toString() || "all"
  );

  const formattedSubheader = (
    <>
      {subheader ? <span className="mr-md">{subheader}</span> : null}

      {showOnlineOrdering ? (
        <span className="text-notification">Free online ordering</span>
      ) : (
        <span className="text-grey">No Leafly online ordering</span>
      )}
    </>
  );

  return (
    <section className={classnames(className)}>
      <Carousel
        cardsPerSlide={cardsPerSlide}
        title={
          <h2>
            <a
              href={headerLink}
              onClick={headerOnClick}
              className="heading--m"
              data-testid="carousel-title"
            >
              {header}
            </a>
          </h2>
        }
        subtitle={
          <span className="block text-xs font-bold text-deep-green">
            {formattedSubheader}
          </span>
        }
        seeMoreHref={actionHref}
        seeMoreText={updatedActionText}
        seeMoreOnClick={actionOnClick}
        data-testid="menu-carousel"
      >
        {loading
          ? new Array(6)
              .fill(" ")
              .map((_, ind) => (
                <MenuCardSkeleton
                  key={ind}
                  className="bg-white elevation-low rounded"
                />
              ))
          : menuItems.map((menuItem) => (
              <TrackImpression
                key={menuItem.id}
                category={Category.menuItemCard}
                className="h-full"
                customDimensions={{
                  dispensaryId: menuItem.dispensaryId,
                }}
              >
                <MenuItemCard
                  handleCardClick={() => cardOnClick(menuItem)}
                  menuItem={menuItem}
                  similarStrain={
                    showSimilarStrains && menuItem?.strain?.name !== shopName
                  }
                >
                  <MenuItemCardActions
                    menuItem={menuItem}
                    viewDetailsOnClick={() => viewDetailsOnClick(menuItem)}
                  />
                </MenuItemCard>
              </TrackImpression>
            ))}
      </Carousel>
    </section>
  );
};

export default MenuItemCarousel;
